import { useMemo } from 'react';

import { isSemanticallyEmpty } from '@core';
import { transformClubTrackToFramesFirst, transformSkeletonToFramesFirst, type Data3D } from '@elva-visualizer';
import { useAnalysisById, useSelectedAnalysis } from '@hooks';
import { useParametersByNames, useSwingAnalysis } from '@queries';

const SWING_PLANE_PARAM_NAMES = [
    'swing_plane_0',
    'swing_plane_1',
    'swing_plane_2',
    'swing_plane_3',
    'swing_plane_4',
    'swing_plane_5',
] as const;

export type SwingPlane = [number, number, number, number, number, number];

export function useSwingPlane(swingId: string | null | undefined): SwingPlane | undefined {
    const { data: swingPlaneParams } = useParametersByNames(swingId ?? undefined, [...SWING_PLANE_PARAM_NAMES]);

    return useMemo(() => {
        if (swingPlaneParams?.length !== 6) return undefined;
        return swingPlaneParams.map(({ value }) => value) as SwingPlane;
    }, [swingPlaneParams]);
}

function transformAnalysisToData3D(
    analysis: ReturnType<typeof useSelectedAnalysis>,
): Omit<Data3D, 'swingPlane' | 'golfBallTranslation' | 'metadata'> | null {
    if (isSemanticallyEmpty(analysis?.data) || !analysis?.data.analysis || analysis.data.analysis.isQuickAnalysis) {
        return null;
    }

    const { analysis: analysisData } = analysis.data;
    const skeleton = transformSkeletonToFramesFirst(analysisData.skeleton ?? {});
    const clubTrack = transformClubTrackToFramesFirst(analysisData.club_track);
    const { bones, segmentation, keypoint_id_to_name: keypointIdToName, measurements } = analysisData;

    return { skeleton, clubTrack, bones, segmentation, keypointIdToName, measurements };
}

export function usePlayback3DData(swingId: string | null | undefined): Data3D | null {
    const analysis = useAnalysisById(swingId);
    const swingPlane = useSwingPlane(swingId);
    const { data: swingAnalysis } = useSwingAnalysis(swingId ?? undefined);
    const golfBallTranslation = swingAnalysis?.metadata?.golfBallTranslation;

    return useMemo(() => {
        const baseData = transformAnalysisToData3D(analysis);
        if (!baseData) return null;

        return { ...baseData, swingPlane, golfBallTranslation };
    }, [analysis, swingPlane, golfBallTranslation]);
}

export function use3dDataStatus(activeSwingId: string | null | undefined) {
    const { data: analysisResp, isLoading: isLoadingAnalysis } = useSwingAnalysis(activeSwingId ?? '');
    const analysis = analysisResp?.fullAnalysis ?? analysisResp?.quickAnalysis;

    return { noAnalysis: isSemanticallyEmpty(analysis?.data), isLoadingAnalysis };
}
